import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FormSection, FieldRow, controlClass } from '../../components/FormSection';
import { Button } from '../../components/Button';
import {
  useOnboarding,
  useSeedDemo,
  useStartOnboardingCheckout,
  useUpdateBusiness,
  useUpdateHow,
  useUpdateInterest
} from './hooks';

// Backend steps: business → how → interest → demo → done. "demo" is the
// try-it-then-subscribe phase; Stripe checkout is launched from there, and
// checkout success lands on /onboarding/profile to finish.
const STEPS = ['business', 'how', 'interest', 'demo'] as const;
const WORK_TYPES = ['Cleaning', 'Landscaping', 'Pet care', 'Handyman', 'Tutoring', 'Other'];
const INTERESTS: { key: string; label: string }[] = [
  { key: 'Receipts', label: 'Send receipts' },
  { key: 'Documents', label: 'E-sign documents' },
  { key: 'Clients', label: 'Manage clients and visits' },
  { key: 'Estimates', label: 'Send estimates' }
];
const DEMO_LINK: Record<string, { to: string; label: string }> = {
  Receipts: { to: '/receipts', label: 'your sample receipt' },
  Documents: { to: '/documents/sent', label: 'your sample document' },
  Clients: { to: '/customers', label: 'your sample client & visits' },
  Estimates: { to: '/estimates', label: 'your sample estimate' }
};

function stepIndex(step: string) {
  const i = STEPS.indexOf(step as (typeof STEPS)[number]);
  return i < 0 ? STEPS.length : i; // "done" sorts last
}

export function OnboardingPage() {
  usePageTitle('Set up ReceiptRaptor');
  const { data: state, isLoading } = useOnboarding();

  if (isLoading || !state) {
    return <div className="max-w-2xl mx-auto px-4 py-10 text-slate-500">Loading setup…</div>;
  }

  const current = stepIndex(state.step);

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 space-y-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">Let's set up ReceiptRaptor</h1>
        <p className="text-slate-500 text-sm mt-1">A few quick questions, a look around, then you're live.</p>
      </div>

      <StepShell n={1} title="Your business" done={current > 0} active={current === 0}>
        <BusinessStep />
      </StepShell>
      <StepShell n={2} title="How you work" done={current > 1} active={current === 1}>
        <HowStep />
      </StepShell>
      <StepShell n={3} title="What brought you here?" done={current > 2} active={current === 2}>
        <InterestStep />
      </StepShell>
      <StepShell n={4} title="See it in action, then subscribe" done={current > 3} active={current === 3}>
        <DemoSubscribeStep interestPath={state.interestPath} demoSeeded={state.demoSeeded} />
      </StepShell>
    </div>
  );
}

function StepShell({
  n,
  title,
  done,
  active,
  children
}: {
  n: number;
  title: string;
  done: boolean;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <FormSection>
      <div className="flex items-center gap-3">
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
            done ? 'bg-success text-white' : active ? 'bg-pine text-white' : 'bg-slate-200 text-slate-500'
          }`}
        >
          {done ? <Check size={14} /> : n}
        </span>
        <h2 className={`font-display text-base font-semibold ${active || done ? 'text-ink' : 'text-slate-400'}`}>
          {title}
        </h2>
      </div>
      {active && <div className="mt-4">{children}</div>}
    </FormSection>
  );
}

function BusinessStep() {
  const { data: state } = useOnboarding();
  const update = useUpdateBusiness();
  const [businessName, setBusinessName] = useState(state?.businessName ?? '');
  const [workType, setWorkType] = useState(state?.workType ?? WORK_TYPES[0]);

  return (
    <div className="space-y-4">
      <FieldRow label="Business name" htmlFor="bn">
        <input id="bn" className={controlClass} value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
      </FieldRow>
      <FieldRow label="What kind of work do you do?" htmlFor="wt">
        <select id="wt" className={controlClass} value={workType} onChange={(e) => setWorkType(e.target.value)}>
          {WORK_TYPES.map((w) => (
            <option key={w} value={w}>{w}</option>
          ))}
        </select>
      </FieldRow>
      <div className="flex justify-end">
        <Button
          disabled={!businessName.trim() || update.isPending}
          onClick={() => update.mutate({ businessName: businessName.trim(), workType })}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

function HowStep() {
  const update = useUpdateHow();
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink">Do you ever serve multiple clients at the same time?</p>
      <div className="flex gap-3">
        <Button variant="secondary" disabled={update.isPending} onClick={() => update.mutate({ overlapAllowed: true })}>
          Yes
        </Button>
        <Button variant="secondary" disabled={update.isPending} onClick={() => update.mutate({ overlapAllowed: false })}>
          No
        </Button>
      </div>
    </div>
  );
}

function InterestStep() {
  const update = useUpdateInterest();
  return (
    <div className="space-y-2">
      {INTERESTS.map((i) => (
        <button
          key={i.key}
          type="button"
          disabled={update.isPending}
          onClick={() => update.mutate({ interestPath: i.key })}
          className="w-full text-left rounded-lg border border-slate-200 px-4 py-3 text-sm text-ink hover:border-pine hover:bg-slate-50"
        >
          {i.label}
        </button>
      ))}
    </div>
  );
}

function DemoSubscribeStep({ interestPath, demoSeeded }: { interestPath: string | null; demoSeeded: boolean }) {
  const seed = useSeedDemo();
  const checkout = useStartOnboardingCheckout();

  // Seed the sample set once on entering this step.
  useEffect(() => {
    if (interestPath && !demoSeeded && !seed.isPending) seed.mutate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interestPath, demoSeeded]);

  const link = interestPath ? DEMO_LINK[interestPath] : undefined;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink">
        We've added sample data so you can try the real app. It's deleted the moment you finish setup.
      </p>
      {seed.isPending && <p className="text-sm text-slate-500">Preparing your demo…</p>}
      {demoSeeded && link && (
        <Link to={link.to} className="inline-block text-pine hover:text-pine-600 text-sm font-medium">
          → Explore {link.label}
        </Link>
      )}

      <div className="rounded-xl border border-slate-200 p-4">
        <div className="font-display text-lg font-semibold text-ink">ReceiptRaptor</div>
        <p className="text-sm text-slate-500 mt-1">
          Flat monthly price. Unlimited clients, receipts, estimates, and documents.
        </p>
      </div>
      <div className="flex justify-end">
        <Button disabled={checkout.isPending} onClick={() => checkout.mutate()}>
          {checkout.isPending ? 'Starting…' : 'Start your subscription'}
        </Button>
      </div>
    </div>
  );
}
